/**
 * Authentication Service for Keycloak Integration
 */

import axios from 'axios';
import { encrypt } from '../utils/encryptionUtils';



interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token?: string;
  'not-before-policy': number;
  session_state: string;
  scope: string;
}

interface DecodedToken {
  sub: string;
  exp?: number;
  iat?: number;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };

  facility?: { id: string; name: string };
  tenantId?: string;
}

export interface ResetOwnPasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/** Runs fn while holding a lock shared by every tab of this site. */
async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = (typeof navigator !== 'undefined' ? (navigator as any).locks : undefined);
  if (locks && typeof locks.request === 'function') {
    return locks.request('carex-token-refresh', fn);
  }
  return fn();
}

class AuthService {
  private refreshPromise: Promise<string | null> | null = null;
  // private tokenEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
  // private logoutEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;

  /**
   * Login with username and password
   */
  async login(username: string, password: string): Promise<{ token: string; user: any }> {
    try {
      const loginUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/users/login`;
      const payload = {
        username: encrypt(username),
        password: encrypt(password)
      };

      const response = await axios.post(
        loginUrl,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Map response to expected format
      const data = response.data;
      // Handle various potential token fields
      const access_token = data.access_token || data.token;
      const refresh_token = data.refresh_token || data.refreshToken;

      if (!access_token) {
        throw new Error('No access token received from server');
      }

      // Decode the JWT token to get user info
      const decodedToken = this.decodeToken(access_token);

      // Store tokens
      localStorage.setItem('authToken', access_token);
      if (refresh_token) {
        localStorage.setItem('refreshToken', refresh_token);
      }

      // Determine user role from token
      const role = this.extractRole(decodedToken);
      const user = {
        id: decodedToken.sub,
        username: decodedToken.preferred_username || username,
        email: decodedToken.email,
        name: decodedToken.name || `${decodedToken.given_name} ${decodedToken.family_name}`,
        role,
        token: access_token,
        facility: decodedToken.facility || null,
        tenantId: decodedToken.tenantId || null
      };

      // Cache role
      const roleCache = JSON.parse(localStorage.getItem('role_cache') || '{}');
      roleCache[username] = role;
      localStorage.setItem('role_cache', JSON.stringify(roleCache));

      return { token: access_token, user };
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      }

      const responseData = error.response?.data;
      const responseStatus = error.response?.status;

      // Handle HTTP 423 Locked / ACCOUNT_LOCKED from backend API
      if (
        responseStatus === 423 ||
        responseData?.status === 423 ||
        responseData?.errorCode === 'ACCOUNT_LOCKED' ||
        responseData?.error === 'Locked'
      ) {
        const lockMessage =
          responseData?.message?.includes('locked')
            ? responseData.message
            : 'Your account has been locked after 3 consecutive failed login attempts. Please contact your administrator to unlock your account.';
        const lockError: any = new Error(lockMessage);
        lockError.status = 423;
        lockError.errorCode = responseData?.errorCode || 'ACCOUNT_LOCKED';
        lockError.isLocked = true;
        throw lockError;
      }

      // Handle HTTP 401 Unauthorized
      if (responseStatus === 401) {
        const errorMessage = responseData?.message || 'Invalid username or password';
        const authError: any = new Error(errorMessage);
        authError.status = 401;
        throw authError;
      }

      // Handle any other backend error responses (e.g. 400, 403, 500)
      if (responseData?.message) {
        const customError: any = new Error(responseData.message);
        customError.status = responseStatus;
        customError.errorCode = responseData?.errorCode;
        throw customError;
      }
      
      throw new Error(error.message || 'Authentication failed. Please try again.');
    }
  }


  /**
   * Refresh the access token using refresh token
   */
  async refreshToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = withRefreshLock(() => this.refreshWithApi());
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Exchanges the refresh token through the API (which holds the Keycloak client secret).
   * Runs inside a lock shared by all tabs, so only one tab refreshes at a time; a tab that
   * waited reuses the token the other tab just saved instead of spending the old one.
   */
  private async refreshWithApi(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;

    const current = localStorage.getItem('authToken');
    const refreshedByAnotherTab = Number(localStorage.getItem('lastTokenRefreshTime') || '0') > Date.now() - 30 * 1000;
    if (current && refreshedByAnotherTab && !this.isTokenExpired(current)) {
      return current;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/v1/users/refresh-token`, { refreshToken });

      const data = response.data;
      const access_token = data.access_token || data.token;
      const new_refresh_token = data.refresh_token || data.refreshToken;

      if (!access_token) {
        throw new Error('No access token received from refresh');
      }

      localStorage.setItem('authToken', access_token);
      if (new_refresh_token) {
        localStorage.setItem('refreshToken', new_refresh_token);
      }
      localStorage.setItem('lastTokenRefreshTime', String(Date.now()));
      return access_token;
    } catch (error: any) {
      console.error('Refresh token error:', error);
      if (error.response && [400, 401, 403].includes(error.response.status)) {
        // Another tab may have refreshed while this request was in flight.
        const latest = localStorage.getItem('authToken');
        if (localStorage.getItem('refreshToken') !== refreshToken && latest && !this.isTokenExpired(latest)) {
          return latest;
        }
        // Don't call this.logout() here — let the caller decide whether to log out.
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
      }
      return null;
    }
  }

  /**
   * Logout from the application
   */
  async logout(): Promise<void> {
    try {
      const logoutUrl = `${process.env.REACT_APP_API_BASE_URL}/v1/users/logout`;
      const token = localStorage.getItem('authToken');

      if (token) {
        await axios.post(
          logoutUrl,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Still proceed with clearing local storage even if API fails
    } finally {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  }

  /**
   * Decode JWT token (public)
   */
  public decodeToken(token: string): any {
    try {
      if (!token || typeof token !== 'string') return null;
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Extract facility UUID from JWT token claims
   */
  public getFacilityIdFromToken(token?: string | null): string | null {
    try {
      const jwt = token || localStorage.getItem('authToken');
      if (!jwt) return null;
      const decoded = this.decodeToken(jwt);
      if (!decoded) return null;

      // 1. Keycloak custom claim: Facilities: [UUID]
      if (Array.isArray(decoded.facilities) && decoded.facilities.length > 0) {
        const fac = decoded.facilities[0];
        if (typeof fac === 'string' && fac.trim()) return fac.trim();
        if (fac && typeof fac === 'object' && fac.id) return fac.id;
      }
      if (typeof decoded.facilities === 'string' && decoded.facilities.trim()) {
        return decoded.facilities.trim();
      }

      // 2. facility claim (object, array, or string)
      if (decoded.facility) {
        if (typeof decoded.facility === 'string' && decoded.facility.trim()) return decoded.facility.trim();
        if (decoded.facility.id) return decoded.facility.id;
        if (Array.isArray(decoded.facility) && decoded.facility.length > 0) {
          const first = decoded.facility[0];
          return first?.id || (typeof first === 'string' ? first : null);
        }
      }

      // 3. facilityId or facility_id
      if (decoded.facilityId && typeof decoded.facilityId === 'string') return decoded.facilityId.trim();
      if (decoded.facility_id && typeof decoded.facility_id === 'string') return decoded.facility_id.trim();

      // 4. assigned_facilities or assignedFacilities
      if (Array.isArray(decoded.assigned_facilities) && decoded.assigned_facilities.length > 0) {
        const first = decoded.assigned_facilities[0];
        if (typeof first === 'string') return first.trim();
      }
      if (Array.isArray(decoded.assignedFacilities) && decoded.assignedFacilities.length > 0) {
        const first = decoded.assignedFacilities[0];
        if (typeof first === 'string') return first.trim();
      }

      return null;
    } catch (e) {
      console.error('Error extracting facility ID from token', e);
      return null;
    }
  }

  /**
   * Extract tenant UUID from JWT token claims
   */
  public getTenantIdFromToken(token?: string | null): string | null {
    try {
      const jwt = token || localStorage.getItem('authToken');
      if (!jwt) return null;
      const decoded = this.decodeToken(jwt);
      if (!decoded) return null;
      return decoded.tenant || decoded.tenantId || decoded.tenant_id || null;
    } catch {
      return null;
    }
  }

  /**
   * Extract user role from token
   */
  private extractRole(decodedToken: DecodedToken): string {
    // Check carex-api resource roles first (client-specific roles)
    const carexApiRoles = decodedToken.resource_access?.['carex-api']?.roles || [];

    // Also check realm roles as fallback
    const realmRoles = decodedToken.realm_access?.roles || [];

    // Combine both role arrays and normalize (uppercase, remove ROLE_ prefix)
    const allRoles = [...carexApiRoles, ...realmRoles].map(r =>
      r.toString().toUpperCase().replace(/^ROLE_/, "")
    );

    if (allRoles.includes('SUPER_ADMIN')) {
      return 'SUPER_ADMIN';
    }
    if (allRoles.includes('CLIENT_ADMIN')) {
      return 'CLIENT_ADMIN';
    }
    if (allRoles.includes('FACILITY_ADMIN')) {
      return 'FACILITY_ADMIN';
    }
    if (allRoles.includes('ADMIN')) {
      return 'ADMIN';
    }
    if (allRoles.includes('DOCTOR')) {
      return 'DOCTOR';
    }
    if (allRoles.includes('LEAD_CAREGIVER')) {
      return 'LEAD_CAREGIVER';
    }
    if (allRoles.includes('CAREGIVER')) {
      return 'CAREGIVER';
    }
    if (allRoles.includes('NURSE')) {
      return 'NURSE';
    }
    if (allRoles.includes('SOCIAL_WORKER')) {
      return 'SOCIAL_WORKER';
    }

    // Default role
    return 'NURSE';
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      // Add a 60-second buffer to prevent tokens expiring during transit
      return decoded['exp'] ? decoded['exp'] < (currentTime + 60) : true;
    } catch {
      return true;
    }
  }

  /**
   * Send forgot password email
   */
  async forgotPassword(email: string): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/forgot-password-mail`;
    try {
      await axios.post(
        url,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to send reset email. Please try again.'
      );
    }
  }

  /**
   * Reset own password (requires auth token)
   */
  async resetOwnPassword(data: ResetOwnPasswordRequest): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/reset-own`;
    
    const encryptedData = {
      currentPassword: encrypt(data.currentPassword),
      newPassword: encrypt(data.newPassword),
      confirmPassword: encrypt(data.confirmPassword)
    };
    
    try {
      const encryptedData = {
        currentPassword: encrypt(data.currentPassword),
        newPassword: encrypt(data.newPassword),
        confirmPassword: encrypt(data.confirmPassword)
      };
      await axios.put(
        url,
        encryptedData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
        }
      );
    } catch (error: any) {
      console.error('Reset own password error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to reset password. Please check your current password and try again.'
      );
    }
  }

  /**
   * Reset password using a token (from email link)
   */
  async resetPasswordWithToken(token: string, username: string, newPassword: string, confirmPassword: string): Promise<void> {
    const authBaseUrl = process.env.REACT_APP_AUTH_BASE_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api$/, '/auth') : 'https://api.carex.medicforce.uk/auth');
    const url = `${authBaseUrl}/reset-password-bymail`;
    try {
      await axios.post(
        url,
        {
          token,
          username: encrypt(username),
          newPassword: encrypt(newPassword),
          confirmPassword: encrypt(confirmPassword)
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error: any) {
      console.error('Reset password with token error:', error);
      throw new Error(
        error.response?.data?.message ||
        'Failed to reset password. The token may be invalid or expired.'
      );
    }
  }
}

export default new AuthService();
